import React, { useState, useContext, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import { format } from "date-fns";
import Container from "../../Container/Container";
import SearchComponent from "../../SearchComponent/SearchComponent";
import TableComponent from "../../TableComponent/TableComponent";
import NoData from "../../NoData/NoData";
import { AuthContext } from "../../../AuthContext";
import { getTableColumns } from "../../../util";
import {
  Autocomplete,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import "./Roles.css";

// API & Authentication
import { fetchRoles } from "../../../apis/roles.api";
import { fetchOrganisations } from "../../../apis/organisations.api";
import { fetchProjects } from "../../../apis/projects.org";
import usePermissions from "../../../hooks/userPermissions";

function Roles() {
  const role = 1;
  const { canRoleCreate, canRoleUpdate, canRoleDelete } = usePermissions();
  const { organisationId, projectId, authData } = useContext(AuthContext);
  const { access_token, token_type } = authData;
  const [searchResults, setSearchResults] = useState([]);
  const [rolesRows, setRolesRows] = useState([]);
  const [rolesColumns, setRolesColumns] = useState([]);
  const [selectedProject, setSelectedProject] = useState("");
  const [selectedOrganisation, setSelectedOrganization] = useState("");

  // create rows Data
  const createRowsData = (data) => {
    return data?.map((item) => {
      return {
        "Role ID": item?.role_id,
        "Role Name": item?.role_name,
        "Role Type": item?.role_type,
        "Role Description": item?.role_description,
        "Created On": format(item.created_on, "MM/dd/yyyy, hh:mm a"),
      };
    });
  };

  const {data: organisationsData, isLoading: loadingOrganisations, isOrgFetching, refetch} = useQuery({
    queryKey: ['organisations', {
      organisationId: organisationId, 
      projectId: projectId,
      access_token: access_token, 
      token_type: token_type
    }], 
    queryFn: fetchOrganisations, 
    refetchOnWindowFocus: false
  })

  const {data: projectsData, isLoading: loadingProjects, isProjFetching} = useQuery({
    queryKey: ['projects', {
      organisationId: organisationId, 
      projectId: projectId,
      access_token: access_token, 
      token_type: token_type
    }], 
    queryFn: fetchProjects, 
    refetchOnWindowFocus: false
  })

  const {
    data: rolesData,
    isLoading: loadingRoles,
    isFetching,
  } = useQuery({
    queryKey: ["roles", { access_token, token_type }], 
    queryFn: fetchRoles,
    refetchOnWindowFocus: false,
  });

  // Handle data processing when rolesData changes
  useEffect(() => {
    if (rolesData) {
      setSearchResults(rolesData);
      const rowsData = createRowsData(rolesData);
      const columnsData = getTableColumns(rowsData);
      setRolesRows(rowsData);
      setRolesColumns(columnsData);
    }
  }, [rolesData]);

  const handleProjectChange = (event, value) => {
    setSelectedProject(value);
  };

  const handleOrganizationChange = (event, value) => {
    setSelectedOrganization(value);
  };

  // Users serach functionality on sereach
  const handleSearchFunctionality = (event) => {
    let resultArray=[];
    const query = event?.toLowerCase();
    if (!query) {
      resultArray = rolesData;
      setSearchResults(rolesData);
    }else{
     resultArray = rolesData?.filter((data) => {
      return data?.role_name?.toLowerCase().includes(query.toLowerCase());
    });
    setSearchResults(resultArray)
  }
    const rowsData = createRowsData(resultArray);
    const columnsData = getTableColumns(rowsData);
    setRolesRows(rowsData);
    setRolesColumns(columnsData);
  };

  return (
    <>
      <Backdrop
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.drawer + 1,
          height: "134vh !important",
        }}
        open={loadingRoles || isFetching}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <Container>
        <div className="rolesContainer">
          <div className="rolesHeader">
            <h2>Your Roles</h2>
          </div>
          <SearchComponent
            placeholder="Search Roles"
            CreateButtonTitle="Create Role"
            canCreate={canRoleCreate}
            onSearch={handleSearchFunctionality}
          />
          <div className="rolesDropdown">
            <Autocomplete
              disablePortal
              options={projectsData?.map(org => org.name) || []}
              value={selectedProject}
              onChange={handleProjectChange}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Select Project"
                  variant="outlined"
                />
              )}
              className="dropdownClass"
            />
            <Autocomplete
              disablePortal
              options={organisationsData?.map(org => org.name) || []}
              value={selectedOrganisation}
              onChange={handleOrganizationChange}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Select Organization"
                  variant="outlined"
                />
              )}
              className="dropdownClass"
            />
          </div>
          {searchResults && rolesColumns.length > 0 && rolesRows.length > 0 && (
            <TableComponent
              columns={rolesColumns}
              rows={rolesRows}
              canUpdate={canRoleUpdate}
              canDelete={canRoleDelete}
            />
          )}
          {searchResults?.length === 0 && <NoData main={"Roles"} />}
        </div>
      </Container>
    </>
  );
}

export default Roles;
